import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Flex, Text, Badge, Button, Skeleton, VStack, Stack, Alert, AlertIcon, Heading, SimpleGrid,
} from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';
import { getNfeDocumentosPendencias } from '../services/api';
import { formatDate, formatDateTime } from '../utils/formatters';
import { textoDeErro } from '../utils/erroApi';
import { TIPO_NOTA, ORIGENS_NOTA, dataLocal, MAX_DIAS_PERIODO } from '../utils/notasFiscais';

const periodoDaPendencia = (p) => {
    const fim = new Date(String(p.mais_recente || p.mais_antiga || '').replace(' ', 'T'));
    const fimValido = isNaN(fim.getTime()) ? new Date() : fim;
    const inicioPermitido = new Date(fimValido.getTime() - MAX_DIAS_PERIODO * 86400000);
    const antiga = new Date(String(p.mais_antiga || '').replace(' ', 'T'));
    const inicio = isNaN(antiga.getTime()) || antiga < inicioPermitido ? inicioPermitido : antiga;
    return { data_inicio: dataLocal(inicio), data_fim: dataLocal(fimValido) };
};

export default function PendenciasNotasFiscais({ onVerNotas }) {
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            setDados(await getNfeDocumentosPendencias());
        } catch (e) {
            setErro(textoDeErro(e?.response?.data) || e.message);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    if (carregando && !dados) {
        return (
            <VStack spacing={3} align="stretch">
                {[...Array(4)].map((_, i) => <Skeleton key={i} h="90px" borderRadius="lg" startColor="gray.100" endColor="gray.200" />)}
            </VStack>
        );
    }

    const semXml = dados?.sem_xml || [];
    const fontes = dados?.fontes || [];

    return (
        <VStack spacing={4} align="stretch">
            <Flex justify="flex-end">
                <Button size="sm" variant="outline" leftIcon={<RefreshCw size={14} />} onClick={carregar} isLoading={carregando}>
                    Atualizar
                </Button>
            </Flex>

            {erro && <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{erro}</Alert>}

            {(dados?.vendas_fora_do_repositorio ?? 0) > 0 && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    {dados.vendas_fora_do_repositorio} nota(s) de venda do sistema ainda não entraram nesta central.
                </Alert>
            )}

            <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" p={{ base: 3, md: 4 }}>
                <Heading size="sm" mb={3}>Sincronização</Heading>
                <SimpleGrid columns={{ base: 1, lg: 3 }} gap={3}>
                    {fontes.map((f) => (
                        <Box key={f.comando} borderRadius="md" border="1px solid" borderColor={f.ultima_falha ? 'red.200' : 'gray.200'} p={3}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">{f.descricao}</Text>
                            <Text fontSize="xs" color="gray.500" mb={2}>
                                {f.ultima_execucao ? `Última execução: ${formatDateTime(f.ultima_execucao, '')}` : 'Ainda não rodou'}
                            </Text>
                            {f.ultima_mensagem && (
                                <Text fontSize="11px" color="gray.600" whiteSpace="pre-wrap" wordBreak="break-word">{f.ultima_mensagem}</Text>
                            )}
                            {f.ultima_falha && (
                                <Alert status="error" borderRadius="md" fontSize="11px" mt={2} alignItems="flex-start">
                                    <AlertIcon boxSize={3.5} mt={0.5} />
                                    <Box>
                                        <Text fontWeight="semibold">Falha em {formatDateTime(f.ultima_falha.em, '')}</Text>
                                        <Text whiteSpace="pre-wrap" wordBreak="break-word">{f.ultima_falha.mensagem}</Text>
                                    </Box>
                                </Alert>
                            )}
                        </Box>
                    ))}
                </SimpleGrid>
            </Box>

            <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" p={{ base: 3, md: 4 }}>
                <Heading size="sm" mb={3}>Notas sem XML</Heading>
                {semXml.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">Todas as notas da central têm o XML.</Text>
                ) : (
                    <Stack spacing={2}>
                        {semXml.map((p) => (
                            <Flex key={`${p.tipo}-${p.origem}`} justify="space-between" align={{ base: 'flex-start', md: 'center' }}
                                direction={{ base: 'column', md: 'row' }} gap={2}
                                borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                                <Box>
                                    <Flex gap={2} align="center" wrap="wrap">
                                        <Badge colorScheme={TIPO_NOTA[p.tipo]?.cor || 'gray'} fontSize="10px">{TIPO_NOTA[p.tipo]?.label || p.tipo}</Badge>
                                        <Text fontSize="sm" fontWeight="semibold">{p.total} nota(s)</Text>
                                        <Text fontSize="xs" color="gray.500">{ORIGENS_NOTA[p.origem] || p.origem}</Text>
                                    </Flex>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        Emitidas de {formatDate(p.mais_antiga, 'data desconhecida')} a {formatDate(p.mais_recente, 'data desconhecida')}
                                    </Text>
                                </Box>
                                <Button size="sm" variant="outline" colorScheme="orange" w={{ base: 'full', md: 'auto' }}
                                    onClick={() => onVerNotas({ tipos: [p.tipo], origem: p.origem, pendentes: true, ...periodoDaPendencia(p) })}>
                                    Ver notas
                                </Button>
                            </Flex>
                        ))}
                    </Stack>
                )}
            </Box>
        </VStack>
    );
}
